import React, { Fragment, useState, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import SubscriptionStatus from "./SubscriptionStatus";
import SubscribeButton from "./SubscribeButton";
import { useSubscription } from "../contexts/SubscriptionContext";
import { doc, updateDoc, getDoc } from "firebase/firestore";
import { db } from "../config/firebase";

interface AccountMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

const AccountMenu: React.FC<AccountMenuProps> = ({ isOpen, onClose }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { isActive } = useSubscription();
  const [darkMode, setDarkMode] = useState(false);
  const [language, setLanguage] = useState("English");

  useEffect(() => {
    const loadUserPreferences = async () => {
      if (user) {
        try {
          const userRef = doc(db, "users", user.uid);
          const userDoc = await getDoc(userRef);
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setDarkMode(userData.darkMode || false);
            setLanguage(userData.language || "English");
          }
        } catch (error) {
          // silent
        }
      }
    };
    loadUserPreferences();
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const toggleDarkMode = async () => {
    if (!user) return;
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    try {
      const userRef = doc(db, "users", user.uid);
      await updateDoc(userRef, { darkMode: newDarkMode });
      if (newDarkMode) {
        document.documentElement.classList.add("dark");
      } else {
        document.documentElement.classList.remove("dark");
      }
    } catch (error) {
      // silent
    }
  };

  const handleLanguageChange = async (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    if (!user) return;
    const newLanguage = e.target.value;
    setLanguage(newLanguage);
    try {
      const userRef = doc(db, "users", user.uid);
      await updateDoc(userRef, { language: newLanguage });
    } catch (error) {
      // silent
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 p-0 text-left align-middle shadow-xl transition-all">
                {/* Header */}
                <div className="flex justify-between items-center px-6 py-5 border-b border-slate-100 dark:border-gray-800">
                  <Dialog.Title
                    as="h3"
                    className="text-base font-semibold text-slate-900 dark:text-white"
                  >
                    Account Settings
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-gray-800 transition-colors"
                  >
                    <i className="fas fa-times text-sm"></i>
                  </button>
                </div>

                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Left: Profile */}
                    <div className="space-y-4">
                      {/* User card */}
                      <div className="flex items-center gap-3 p-4 bg-slate-50 dark:bg-gray-950 rounded-2xl border border-slate-100 dark:border-gray-800">
                        <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                          {user?.photoURL ? (
                            <img
                              src={user.photoURL}
                              alt={user.displayName || "User"}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-blue-500">
                              <i className="fas fa-user text-white"></i>
                            </div>
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-slate-900 dark:text-white truncate">
                            {user?.displayName}
                          </p>
                          <p className="text-sm text-slate-400 dark:text-slate-600 truncate mt-0.5">
                            {user?.email}
                          </p>
                        </div>
                      </div>

                      {isActive ? <SubscriptionStatus /> : <SubscribeButton />}
                    </div>

                    {/* Right: Preferences */}
                    <div>
                      <p className="text-xs font-semibold text-slate-400 dark:text-slate-600 uppercase tracking-wider mb-3">
                        Preferences
                      </p>
                      <div className="space-y-2">
                        {/* Dark Mode */}
                        <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-gray-950 rounded-xl border border-slate-100 dark:border-gray-800 hover:border-slate-200 dark:hover:border-gray-700 transition-colors">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-slate-200 dark:bg-gray-800 flex items-center justify-center">
                              <i className="fas fa-moon text-slate-500 dark:text-slate-400 text-xs"></i>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-slate-800 dark:text-slate-200">
                                Dark Mode
                              </p>
                              <p className="text-xs text-slate-400 dark:text-slate-600 mt-0.5">
                                Switch to dark theme
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={toggleDarkMode}
                            role="switch"
                            aria-checked={darkMode}
                            className={`relative w-11 h-6 rounded-full transition-colors duration-200 focus:outline-none flex-shrink-0 ${
                              darkMode
                                ? "bg-[#47b3fe]"
                                : "bg-slate-200 dark:bg-gray-700"
                            }`}
                          >
                            <div
                              className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-200 ${
                                darkMode ? "translate-x-5" : "translate-x-0.5"
                              }`}
                            />
                          </button>
                        </div>

                        {/* Language */}
                        <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-gray-950 rounded-xl border border-slate-100 dark:border-gray-800 hover:border-slate-200 dark:hover:border-gray-700 transition-colors">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-slate-200 dark:bg-gray-800 flex items-center justify-center">
                              <i className="fas fa-globe text-slate-500 dark:text-slate-400 text-xs"></i>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-slate-800 dark:text-slate-200">
                                Language
                              </p>
                              <p className="text-xs text-slate-400 dark:text-slate-600 mt-0.5">
                                Preferred language
                              </p>
                            </div>
                          </div>
                          <select
                            value={language}
                            onChange={handleLanguageChange}
                            className="text-sm bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 text-slate-800 dark:text-slate-200 rounded-lg py-1.5 px-3 focus:outline-none focus:ring-2 focus:ring-[#47b3fe] focus:border-transparent"
                          >
                            <option value="English">English</option>
                            <option value="Turkish">Turkish</option>
                            <option value="Spanish">Spanish</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-slate-100 dark:border-gray-800 flex justify-end gap-2">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-gray-800 hover:bg-slate-200 dark:hover:bg-gray-700 rounded-xl transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleLogout}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-xl transition-colors flex items-center gap-2"
                  >
                    <i className="fas fa-arrow-right-from-bracket text-xs"></i>
                    Sign out
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default AccountMenu;
